<script setup lang="ts">
import type { CraftImageProps } from '@studio-fes/layer-craft/app/components/CraftImage.vue'
import type { CraftVideoProps } from '@studio-fes/layer-craft/app/components/CraftVideo.vue'
import type { MediaFragment } from '#graphql-operations'

const props = defineProps<{
  media: MediaFragment | null | undefined
  imgProps?: CraftImageProps
  videoProps?: CraftVideoProps
}>()

const isVideo = computed(() => props.media?.kind === 'video')
const isImage = computed(() => props.media?.kind === 'image')

// Prefer the CMS asset's own alt/title; fall back to whatever the caller
// passed in `imgProps.alt` (e.g. a nearby heading) only if both are empty.
const resolvedImgProps = computed(() => ({
  ...props.imgProps,
  fit: 'scale-down',
  alt: props.media?.alt || props.media?.title || props.imgProps?.alt,
}))

const imgRef = useTemplateRef('imgRef')
const isLoaded = ref(false)

// The `@load` event doesn't fire for images the browser already has (cache,
// SSR markup, or a decode that finished before `<LazyCraftImage>` resolved and
// attached the listener). Without this check those images stay stuck at
// `opacity-0`. `img.complete` + `naturalWidth` tells us it's already there.
function syncLoaded() {
  const el = imgRef.value?.$el as HTMLImageElement | undefined
  if (el?.complete && el.naturalWidth > 0)
    isLoaded.value = true
}

onMounted(syncLoaded)
watch(imgRef, syncLoaded, { flush: 'post' })
</script>

<template>
  <LazyCraftImage
    v-if="isImage"
    ref="imgRef"
    v-bind="resolvedImgProps"
    :image="media"
    class="motion-relaxed"
    :class="{ 'opacity-0': !isLoaded, 'opacity-100': isLoaded }"
    @load="isLoaded = true"
    @error="isLoaded = true"
  />
  <LazyCraftVideo
    v-else-if="isVideo"
    v-bind="videoProps"
    :video="media"
  >
    <template #default="{ progress, isPlaying }">
      <slot name="media-chrome" :progress :is-playing />
    </template>
  </LazyCraftVideo>
  <slot v-else name="empty">
    
  </slot>
</template>
